import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, RotateCcw, XCircle, AlertTriangle, Send, Loader2 } from 'lucide-react';

export interface RejectReasonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  bookTitle: string;
  actionType: 'publisher_reject' | 'admin_reject';
  isPending?: boolean;
}

const PUBLISHER_QUICK_REASONS = [
  'Format naskah belum sesuai standar redaksi penerbit',
  'Mohon lengkapi sinopsis lengkap dan daftar isi naskah',
  'Genre naskah saat ini tidak sesuai lini terbitan kami',
  'Perlu perbaikan pada ejaan nama penulis atau judul naskah',
  'Mohon cantumkan naskah final yang telah selesai disunting',
];

const ADMIN_QUICK_REASONS = [
  'Data metadata buku belum lengkap atau tidak valid',
  'Informasi naskah tidak memenuhi kriteria standar UIBN',
  'Nomor ISBN asal tidak cocok dengan data pendaftaran',
  'Kategori atau bidang ilmu buku perlu disesuaikan',
];

export const RejectReasonModal: React.FC<RejectReasonModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  bookTitle,
  actionType,
  isPending = false,
}) => {
  const [reason, setReason] = useState('');

  // Reset reason when modal opens or closes
  useEffect(() => {
    if (isOpen) {
      setReason('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isPublisher = actionType === 'publisher_reject';
  const quickReasons = isPublisher ? PUBLISHER_QUICK_REASONS : ADMIN_QUICK_REASONS;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const finalReason =
      reason.trim() ||
      (isPublisher
        ? 'Naskah memerlukan peninjauan dan perbaikan metadata/kelengkapan sebelum diajukan kembali.'
        : 'Pengajuan naskah belum memenuhi kriteria pendaftaran UIBN resmi.');
    onConfirm(finalReason);
  };

  const handleSelectQuickReason = (text: string) => {
    if (!reason.trim()) {
      setReason(text);
    } else {
      setReason((prev) => `${prev.trim()}\n• ${text}`);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-xs">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col"
          role="dialog"
          aria-modal="true"
          aria-labelledby="reject-modal-title"
        >
          {/* Modal Header */}
          <div className={`px-5 py-4 flex items-center justify-between border-b ${
            isPublisher ? 'bg-amber-50/70 border-amber-100' : 'bg-rose-50/70 border-rose-100'
          }`}>
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 shadow-2xs ${
                isPublisher ? 'bg-amber-500 text-white' : 'bg-rose-600 text-white'
              }`}>
                {isPublisher ? (
                  <RotateCcw className="w-5 h-5" />
                ) : (
                  <XCircle className="w-5 h-5" />
                )}
              </div>
              <div>
                <h3 id="reject-modal-title" className="text-sm sm:text-base font-bold text-slate-900 leading-tight">
                  {isPublisher ? 'Tolak / Minta Revisi Naskah' : 'Tolak Pengajuan UIBN'}
                </h3>
                <p className="text-xs text-slate-500 truncate max-w-xs sm:max-w-sm mt-0.5">
                  Buku: <span className="font-semibold text-slate-700">{bookTitle}</span>
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-white rounded-lg transition cursor-pointer disabled:opacity-50"
              title="Tutup Modal"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            {/* Context Notice */}
            <div className={`p-3 rounded-xl text-xs flex items-start gap-2.5 border ${
              isPublisher
                ? 'bg-amber-50/60 border-amber-200/80 text-amber-900'
                : 'bg-rose-50/60 border-rose-200/80 text-rose-900'
            }`}>
              <AlertTriangle className={`w-4 h-4 shrink-0 mt-0.5 ${
                isPublisher ? 'text-amber-600' : 'text-rose-600'
              }`} />
              <div className="space-y-1 leading-relaxed">
                {isPublisher ? (
                  <p>
                    Naskah akan dikembalikan ke status <strong>Draft</strong> untuk penulis. Penulis akan menerima notifikasi beserta catatan revisi Anda agar dapat memperbaikinya.
                  </p>
                ) : (
                  <p>
                    Pengajuan UIBN naskah ini akan ditolak resmi oleh Admin. Penulis dan penerbit dapat melihat alasan penolakan dan mengajukan ulang setelah diperbaiki.
                  </p>
                )}
              </div>
            </div>

            {/* Quick Reason Suggestions */}
            <div>
              <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wide block mb-1.5">
                Pilih Alasan Cepat (Opsional):
              </label>
              <div className="flex flex-wrap gap-1.5">
                {quickReasons.map((qr) => (
                  <button
                    key={qr}
                    type="button"
                    onClick={() => handleSelectQuickReason(qr)}
                    disabled={isPending}
                    className="text-[11px] px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-indigo-50 text-slate-700 hover:text-indigo-700 border border-slate-200 hover:border-indigo-200 transition text-left cursor-pointer"
                  >
                    + {qr}
                  </button>
                ))}
              </div>
            </div>

            {/* Textarea Input */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label htmlFor="reject-reason-input" className="text-xs font-bold text-slate-800">
                  {isPublisher ? 'Alasan Penolakan / Catatan Revisi untuk Penulis:' : 'Alasan Penolakan Pengajuan:'}
                </label>
                <span className="text-[10px] text-slate-400">
                  {reason.length}/500 Karakter (Opsional)
                </span>
              </div>
              <textarea
                id="reject-reason-input"
                rows={4}
                maxLength={500}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={isPending}
                placeholder={
                  isPublisher
                    ? 'Tuliskan catatan alasan penolakan atau bagian yang perlu direvisi oleh penulis...'
                    : 'Tuliskan alasan penolakan pengajuan naskah...'
                }
                className="w-full text-xs p-3 bg-slate-50 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 focus:bg-white text-slate-800 placeholder:text-slate-400 transition"
              />
            </div>

            {/* Modal Actions */}
            <div className="pt-2 flex items-center justify-end gap-2 border-t border-slate-100">
              <button
                type="button"
                onClick={onClose}
                disabled={isPending}
                className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition cursor-pointer disabled:opacity-50"
              >
                Batal
              </button>

              <button
                type="submit"
                disabled={isPending}
                className={`px-4 py-2 text-xs font-bold rounded-xl transition flex items-center gap-1.5 shadow-xs cursor-pointer text-white disabled:opacity-50 ${
                  isPublisher
                    ? 'bg-amber-600 hover:bg-amber-700 active:bg-amber-800'
                    : 'bg-rose-600 hover:bg-rose-700 active:bg-rose-800'
                }`}
              >
                {isPending ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>Memproses...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-3.5 h-3.5" />
                    <span>{isPublisher ? 'Kirim Penolakan / Revisi' : 'Konfirmasi Tolak Pengajuan'}</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
